import { useEffect, useState } from "react";
import { apiCall, ApiError } from "@/lib/api";

interface ProjectOption {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
}

interface Props {
  onClose: () => void;
  onCreated: (cohortId: string) => void;
  /** Pre-selects the cohort's project on open. If omitted, the first
   *  project in the user's accessible list is selected. */
  defaultProjectId?: string;
  /** When set, restricts the project picker to projects under this client.
   *  Used by AdminClientsPage so creating a cohort from inside a client
   *  expansion stays scoped to that client. */
  scopeClientId?: string;
}

/**
 * Shared "create cohort" modal used by both AdminCohortsPage and (newly)
 * AdminClientsPage. Encapsulates the project picker + name input + the
 * libraryCohortSave POST so both pages can spawn the same flow.
 */
export function CreateCohortModal({ onClose, onCreated, defaultProjectId, scopeClientId }: Props): JSX.Element {
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectId, setProjectId] = useState<string>(defaultProjectId ?? "");
  const [name, setName] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async (): Promise<void> => {
      try {
        const body: Record<string, unknown> = {};
        if (scopeClientId !== undefined && scopeClientId !== "") body["clientId"] = scopeClientId;
        const res = await apiCall<{ projects: ProjectOption[] }>("/libraryProjects", { method: "POST", body, timeoutMs: 120_000 });
        if (!alive) return;
        setProjects(res.projects);
        if (projectId === "" && res.projects[0] !== undefined) {
          setProjectId(defaultProjectId ?? res.projects[0].id);
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : "Failed to load projects");
      }
    })();
    return () => { alive = false; };
  }, [defaultProjectId, scopeClientId, projectId]);

  const submit = async (): Promise<void> => {
    if (projectId === "" || name.trim() === "") return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiCall<{ cohortId: string }>("/libraryCohortSave", {
        method: "POST", body: { projectId, name: name.trim() }, timeoutMs: 60_000,
      });
      onCreated(res.cohortId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 text-lg font-bold">New cohort</h3>
        <p className="mb-4 text-xs text-white/60">A cohort lives under a project and holds the learners + sims that run together.</p>

        <label className="mb-3 block">
          <span className="text-xs uppercase tracking-wider text-white/50">Project</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-white/30">
            {projects.length === 0 && <option value="">— no projects yet —</option>}
            {projects.map((p) => (
              <option key={p.id} value={p.id} className="bg-neusim-bg">
                {scopeClientId === undefined ? `${p.clientName} · ${p.name}` : p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="mb-4 block">
          <span className="text-xs uppercase tracking-wider text-white/50">Cohort name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus
                 placeholder="e.g. Q3 Leaders" className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-white/30" />
        </label>

        {error !== null && <div className="mb-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10">Cancel</button>
          <button type="button" disabled={saving || projectId === "" || name.trim() === ""} onClick={() => void submit()}
                  className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/20 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:bg-fuchsia-500/30 disabled:opacity-50">
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
